// CBT Cognitive Distortions - Common thinking patterns identified in Cognitive Behavioural Therapy

export interface CognitiveDistortion {
  id: string;
  name: string;
  description: string;
  example: string;
  challenge: string;
  colour: string;
  // Educational content
  whyItDevelops: string;
  impact: string;
  changeStrategy: string;
}

export const COGNITIVE_DISTORTIONS: CognitiveDistortion[] = [
  {
    id: 'all-or-nothing',
    name: 'All-or-Nothing Thinking',
    description: 'Seeing things in black-and-white categories with no middle ground',
    example: '"If I\'m not perfect, I\'m a total failure"',
    challenge: 'What would a "middle ground" perspective look like here?',
    colour: 'hsl(var(--chart-1))',
    whyItDevelops: 'Often develops in environments where perfectionism was valued or where there was little room for mistakes. When "good enough" wasn\'t celebrated, we learned that anything less than perfect equals failure.',
    impact: 'Creates impossible standards, leads to procrastination (if I can\'t do it perfectly, why try?), and makes setbacks feel catastrophic rather than normal parts of growth.',
    changeStrategy: 'Practice noticing the grey areas. Ask yourself: "On a scale of 0-100%, where does this actually fall?" Most things land somewhere in the middle.'
  },
  {
    id: 'catastrophising',
    name: 'Catastrophising',
    description: 'Expecting the worst possible outcome in any situation',
    example: '"This headache is probably a brain tumour"',
    challenge: 'What is the most likely outcome? What evidence supports that?',
    colour: 'hsl(var(--chart-2))',
    whyItDevelops: 'Can develop from experiences where unexpected bad things happened, creating a protective hypervigilance. If we\'ve been blindsided before, our brain tries to prepare us by imagining worst cases.',
    impact: 'Triggers unnecessary anxiety, keeps the nervous system in constant alert, and can lead to avoidance behaviours that shrink our world.',
    changeStrategy: 'Ask: "What\'s the most likely outcome?" Write down the worst, best, and most realistic scenarios. Usually, reality falls closer to the middle.'
  },
  {
    id: 'mind-reading',
    name: 'Mind Reading',
    description: 'Assuming you know what others are thinking without evidence',
    example: '"They think I\'m boring"',
    challenge: 'What evidence do I actually have? Have I asked them directly?',
    colour: 'hsl(var(--chart-3))',
    whyItDevelops: 'Often develops when we had to anticipate others\' moods or reactions to stay safe or avoid conflict. Children of unpredictable parents often become skilled at "reading" others - but this skill can become overactive.',
    impact: 'Creates social anxiety, damages relationships through assumptions, and prevents authentic connection because we respond to imagined thoughts rather than reality.',
    changeStrategy: 'Remind yourself: "I cannot read minds." Consider alternative explanations for others\' behaviour. When possible, ask directly.'
  },
  {
    id: 'fortune-telling',
    name: 'Fortune Telling',
    description: 'Predicting negative outcomes as if they\'re certain',
    example: '"I know the interview will go badly"',
    challenge: 'Can I really predict the future? What other outcomes are possible?',
    colour: 'hsl(var(--chart-4))',
    whyItDevelops: 'May develop as a way to prepare for disappointment or protect ourselves from hope that feels risky. If we\'ve been let down often, expecting the worst feels safer than daring to hope.',
    impact: 'Becomes a self-fulfilling prophecy (expecting failure affects performance), prevents us from trying new things, and robs us of excitement about possibilities.',
    changeStrategy: 'Challenge yourself: "Have my predictions always been accurate?" List times things turned out better than expected. Practice holding uncertainty with curiosity rather than fear.'
  },
  {
    id: 'should-statements',
    name: 'Should Statements',
    description: 'Using "should", "must", or "ought to" creating pressure and guilt',
    example: '"I should always be productive"',
    challenge: 'Says who? What would happen if I replaced "should" with "could" or "want to"?',
    colour: 'hsl(var(--chart-5))',
    whyItDevelops: 'Often internalised from critical voices in childhood - parents, teachers, or society\'s expectations. These become our inner critic, demanding standards that may never have been ours to begin with.',
    impact: 'Creates guilt, shame, and resentment. "Should" toward ourselves creates self-criticism; "should" toward others creates anger and disappointment.',
    changeStrategy: 'Replace "should" with "could" or "would like to." Ask: "Where did this rule come from? Is it actually my value?" Give yourself permission to have preferences rather than obligations.'
  },
  {
    id: 'emotional-reasoning',
    name: 'Emotional Reasoning',
    description: 'Believing something is true because it feels true',
    example: '"I feel stupid, so I must be stupid"',
    challenge: 'Feelings are not facts. What would a friend say about this situation?',
    colour: 'hsl(var(--destructive))',
    whyItDevelops: 'Can develop when our emotions weren\'t validated, leading us to over-rely on them as truth. Alternatively, if logic was used against us, emotions may feel like the only trustworthy guide.',
    impact: 'Emotions become "evidence" of reality, leading to decisions based on temporary states. Feeling anxious becomes proof something is dangerous.',
    changeStrategy: 'Practice the phrase: "I feel X, but that doesn\'t mean it\'s true." Look for objective evidence separate from how you feel. Emotions are valid signals, but not always accurate reporters.'
  },
  {
    id: 'labelling',
    name: 'Labelling',
    description: 'Attaching a fixed label to yourself or others based on one instance',
    example: '"I made a mistake, so I\'m a loser"',
    challenge: 'Would I say this to a friend? Does one action define my whole identity?',
    colour: 'hsl(var(--primary))',
    whyItDevelops: 'Often mirrors the labels others placed on us, or reflects a harsh inner critic we developed to stay safe. Labels feel certain in an uncertain world, even when they\'re limiting.',
    impact: 'Creates fixed identity that\'s hard to grow beyond. Labels prevent seeing complexity and change. "I\'m lazy" stops us from exploring what\'s actually getting in the way.',
    changeStrategy: 'Separate behaviour from identity: "I did something lazy" vs "I am lazy." Ask: "Is this label the whole truth? What evidence contradicts it?"'
  },
  {
    id: 'mental-filter',
    name: 'Mental Filter',
    description: 'Focusing only on negatives while filtering out positives',
    example: '"I got one criticism, so the whole day was terrible"',
    challenge: 'What positives am I ignoring? Can I list three good things about today?',
    colour: 'hsl(var(--secondary))',
    whyItDevelops: 'The brain\'s negativity bias can become amplified when we\'ve experienced repeated criticism or disappointment. We learn to scan for threats and dismiss the good.',
    impact: 'Creates a distorted view of reality weighted toward the negative. Achievements are minimised while problems are magnified, leading to chronic dissatisfaction.',
    changeStrategy: 'Practice deliberate attention to positives. Keep a daily gratitude list. When something negative happens, ask: "What else is true right now?"'
  },
  {
    id: 'discounting-positives',
    name: 'Discounting Positives',
    description: 'Dismissing positive experiences as not counting',
    example: '"They only said that to be nice"',
    challenge: 'Why might this positive feedback be genuine? What evidence supports it?',
    colour: 'hsl(var(--accent))',
    whyItDevelops: 'May develop when praise felt conditional or unsafe, making it hard to trust genuine positivity. Sometimes linked to imposter syndrome - if we don\'t believe we deserve good things, we explain them away.',
    impact: 'Prevents us from building confidence, enjoying achievements, or internalising positive feedback. Keeps self-esteem perpetually low despite evidence to the contrary.',
    changeStrategy: 'Practice receiving compliments with a simple "thank you." Keep a record of positive feedback and achievements. Ask: "If a friend received this praise, would I think it was earned?"'
  },
  {
    id: 'personalisation',
    name: 'Personalisation',
    description: 'Blaming yourself for things outside your control',
    example: '"It\'s my fault my friend is upset"',
    challenge: 'What factors outside my control contributed? What is actually my responsibility?',
    colour: 'hsl(var(--muted-foreground))',
    whyItDevelops: 'Often develops in childhood when we made sense of chaos by assuming responsibility for things beyond our control. If we caused it, at least we had some power.',
    impact: 'Creates excessive guilt and responsibility for others\' emotions. Leads to people-pleasing, burnout, and difficulty setting boundaries.',
    changeStrategy: 'Practice the "responsibility pie" - draw a circle and allocate percentages to all factors involved, including those outside your control. Your slice is rarely 100%.'
  },
  {
    id: 'overgeneralisation',
    name: 'Overgeneralisation',
    description: 'Drawing broad conclusions from a single event',
    example: '"I failed once, so I always fail"',
    challenge: 'Is this really always true? Can I think of times this wasn\'t the case?',
    colour: 'hsl(var(--chart-1))',
    whyItDevelops: 'The mind looks for patterns to predict the future - sometimes it overfits from limited experiences. One bad outcome becomes "proof" of a permanent pattern.',
    impact: 'Prevents learning from individual situations, creates hopelessness ("nothing ever works out"), and stops us from trying again.',
    changeStrategy: 'Notice words like "always," "never," "everyone," "no one." Replace with specific, accurate language. Look for exceptions to the "rule" your mind has created.'
  },
  {
    id: 'magnification',
    name: 'Magnification/Minimisation',
    description: 'Blowing things out of proportion or shrinking their importance',
    example: '"My mistake is huge but my successes don\'t matter"',
    challenge: 'Am I giving this the right amount of importance? What would a balanced view look like?',
    colour: 'hsl(var(--chart-2))',
    whyItDevelops: 'Can develop when our struggles were dismissed or when we learned to minimise our needs to avoid being a burden. The flip side - magnifying problems - often comes from anxiety.',
    impact: 'Creates an imbalanced view where negatives loom large and positives shrink. Leads to anxiety about small issues and inability to celebrate real achievements.',
    changeStrategy: 'Ask: "How will I feel about this in a week? A year?" Practice giving positive events the same weight you give negative ones. Use a 1-10 scale to calibrate importance.'
  }
];

export const EMOTIONS = [
  'Anxious', 'Sad', 'Angry', 'Frustrated', 'Overwhelmed', 
  'Hopeless', 'Guilty', 'Ashamed', 'Fearful', 'Lonely',
  'Irritable', 'Disappointed', 'Jealous', 'Embarrassed', 'Confused'
];
